import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

const sizeStyles = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[calc(100vw-2rem)]',
} as const;

export type ModalSize = keyof typeof sizeStyles;

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  size?: ModalSize;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  showCloseButton?: boolean;
  className?: string;
  /** ID d’un titre visible dans le contenu (accessibilité). */
  ariaLabelledBy?: string;
  /** Masque la modale pendant une capture d’écran externe (html2canvas). */
  hideForExternalCapture?: boolean;
  /** Toujours exclure de la capture bug report (modale de signalement). */
  excludeFromBugReportCapture?: boolean;
  /** Classes appliquées au conteneur plein écran (backdrop + centrage). */
  overlayClassName?: string;
  children: React.ReactNode;
}

export interface ModalSectionProps {
  className?: string;
  children: React.ReactNode;
}

export function Modal({
  open,
  onClose,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEsc = true,
  showCloseButton = true,
  className,
  ariaLabelledBy,
  hideForExternalCapture = false,
  excludeFromBugReportCapture = false,
  overlayClassName,
  children,
}: ModalProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEsc) {
        onClose();
        return;
      }

      // Focus trap
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last?.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first?.focus();
          }
        }
      }
    },
    [closeOnEsc, onClose]
  );

  const hasAutoFocusedRef = useRef(false);

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';

      // Only auto-focus once when the modal first opens
      if (!hasAutoFocusedRef.current) {
        hasAutoFocusedRef.current = true;
        requestAnimationFrame(() => {
          const focusable = dialogRef.current?.querySelector<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          focusable?.focus();
        });
      }
    } else {
      hasAutoFocusedRef.current = false;
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return createPortal(
    <AnimatePresence>
      {open && (
          <div
          className={cn('fixed inset-0 z-50 flex items-center justify-center p-4', overlayClassName)}
          {...(hideForExternalCapture || excludeFromBugReportCapture
            ? { 'data-hide-for-bug-report-capture': '' }
            : {})}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            aria-hidden="true"
            onClick={closeOnBackdrop ? onClose : undefined}
          />

          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={ariaLabelledBy || undefined}
            aria-label={ariaLabelledBy ? undefined : t('a11y.dialog', 'Dialog')}
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={cn(
              'relative z-10 flex w-full max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-xl bg-white shadow-xl',
              'dark:border dark:border-gray-700 dark:bg-gray-900',
              sizeStyles[size],
              className,
            )}
          >
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className={cn(
                  'absolute end-3 top-3 z-10 rounded-lg p-1.5 text-gray-400',
                  'hover:bg-gray-100 hover:text-gray-600',
                  'dark:hover:bg-gray-800 dark:hover:text-gray-300',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
                )}
                aria-label={t('a11y.closeDialog', 'Close dialog')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export function ModalHeader({ className, children }: ModalSectionProps) {
  return (
    <div
      className={cn(
        'shrink-0 border-b border-gray-200 px-6 py-4 dark:border-gray-700',
        className
      )}
    >
      {children}
    </div>
  );
}

export function ModalBody({ className, children }: ModalSectionProps) {
  return (
    <div className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4', className)}>
      {children}
    </div>
  );
}

export function ModalFooter({ className, children }: ModalSectionProps) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-end gap-3 border-t border-gray-200 bg-white px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-gray-700 dark:bg-gray-900',
        className
      )}
    >
      {children}
    </div>
  );
}

Modal.displayName = 'Modal';
ModalHeader.displayName = 'ModalHeader';
ModalBody.displayName = 'ModalBody';
ModalFooter.displayName = 'ModalFooter';
